import { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import http from "../Utils/axiosConfig";
import { supplyToRow, fmtNum, fmtDate, toNum, rangeTag, EXPORT_LIMIT, buildStyledSheet } from "../Utils/excelExport";
import "./MemberRegister/Details/Details.css";
import "./MemberRegister/Main/Dashboard.css";

const PAGE_SIZE = 15;

const writeSupplyExcel = (records, balance, from, to) => {
  if (!records.length) return false;
  const summaryAoa = [
    ["एकूण शिल्लक (₹)", toNum(balance)],
    ["एकूण नोंदी", records.length],
    ["एकूण रक्कम (₹)", toNum(records.reduce((s, r) => s + Number(r.total_rate || 0), 0))],
    ["एकूण कपात (₹)", toNum(records.reduce((s, r) => s + Number(r.debit || 0), 0))],
  ];
  const ws = buildStyledSheet({ rows: records.map(supplyToRow), summaryAoa, from, to });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "पशुखाद्य");
  XLSX.writeFile(wb, `pashukhady-${rangeTag(from, to)}.xlsx`);
  return true;
};

const SupplyView = ({ mem_id }) => {
  // No date filter by default — a ledger is most useful whole. The filter is opt-in.
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [data, setData] = useState({ balance: 0, records: [], total: 0 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [rangeTotals, setRangeTotals] = useState({ amt: 0, debit: 0, count: 0 });
  const [dlOpen, setDlOpen] = useState(false);
  const [dlLoading, setDlLoading] = useState(false);

  const fetchData = async (pageNum, f = from, t = to) => {
    setLoading(true);
    try {
      const res = await http.post("data/get_supply", { mem_id, from_date: f, to_date: t, page: pageNum, limit: PAGE_SIZE });
      if (res.code === "DATA_FOUND") setData(res.data);
      else setData({ balance: 0, records: [], total: 0 });
    } catch (err) {
      console.error("Error fetching supply:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRangeTotals = async (f, t) => {
    try {
      const res = await http.post("data/get_supply", { mem_id, from_date: f, to_date: t, page: 1, limit: EXPORT_LIMIT });
      if (res.code === "DATA_FOUND") {
        const all = res.data.records || [];
        setRangeTotals({
          count: res.data.total || all.length,
          amt: all.reduce((s, r) => s + Number(r.total_rate || 0), 0),
          debit: all.reduce((s, r) => s + Number(r.debit || 0), 0),
        });
      } else {
        setRangeTotals({ amt: 0, debit: 0, count: 0 });
      }
    } catch (err) {
      console.error("Error fetching supply range totals:", err);
    }
  };

  // Owns every page-driven fetch, including the first load. The previous
  // `if (page !== 1)` guard meant paging back to page 1 never refetched, so the
  // table kept showing page 2's rows while the indicator read "1 / N".
  useEffect(() => {
    fetchData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => {
    fetchRangeTotals(from, to);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    // Setting page to 1 only refires the effect when the value actually
    // changes, so fetch directly when already on page 1.
    if (page === 1) fetchData(1, from, to);
    else setPage(1);
    fetchRangeTotals(from, to);
  };

  const handleDownloadPage = () => { setDlOpen(false); writeSupplyExcel(data.records, data.balance, from, to); };
  const handleDownloadAll = async () => {
    setDlLoading(true);
    try {
      const res = await http.post("data/get_supply", { mem_id, from_date: from, to_date: to, page: 1, limit: EXPORT_LIMIT });
      const all = res.code === "DATA_FOUND" ? res.data.records || [] : [];
      if (res.code === "DATA_FOUND" && (res.data.total || 0) > all.length) {
        alert(`या कालावधीत ${res.data.total} नोंदी आहेत; पहिल्या ${all.length} डाउनलोड होत आहेत. कृपया कालावधी लहान करा.`);
      }
      writeSupplyExcel(all, data.balance, from, to);
    } catch (err) { console.error(err); }
    finally { setDlLoading(false); setDlOpen(false); }
  };

  const totalPages = Math.ceil((data.total || 0) / PAGE_SIZE);

  return (
    <div className="table-container">
      {/* Date filter is opt-in, so closed it costs one content-width pill */}
      <div className="filter-accordion">
        <button className="filter-accordion-head" onClick={() => setShowFilter((s) => !s)}
          aria-expanded={showFilter} title="कालावधी बदला">
          <span className="fa-label">📅 कालावधी</span>
          <span className="fa-value">{from && to ? `${fmtDate(from)} — ${fmtDate(to)}` : "सर्व नोंदी"}</span>
          <span className="fa-action">{showFilter ? "बंद करा ▲" : "बदला ▼"}</span>
        </button>
        <button className="download-btn" onClick={() => setDlOpen(true)}
          disabled={loading || data.records.length === 0} title="Excel डाउनलोड करा">
          ⬇ डाउनलोड
        </button>
      </div>

      {showFilter && (
        <div className="pr-filter-bar">
          <div className="pr-filter-group">
            <label>पासून</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div className="pr-filter-group">
            <label>पर्यंत</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <button className="pr-search-btn" onClick={handleSearch} disabled={loading}>
            {loading ? "..." : "शोधा"}
          </button>
        </div>
      )}

      {/* Summary cards — full range, above table */}
      {!loading && (rangeTotals.count > 0 || data.balance !== 0) && (
        <div className="summary-cards">
          <div className="summary-card">
            <span className="summary-card-label">एकूण नोंदी</span>
            <span className="summary-card-value white">{rangeTotals.count}</span>
          </div>
          <div className="summary-card">
            <span className="summary-card-label">एकूण रक्कम (₹)</span>
            <span className="summary-card-value">₹{fmtNum(rangeTotals.amt)}</span>
          </div>
          <div className="summary-card">
            <span className="summary-card-label">एकूण कपात (₹)</span>
            <span className="summary-card-value red">₹{fmtNum(rangeTotals.debit)}</span>
          </div>
          <div className="summary-card">
            <span className="summary-card-label">शिल्लक</span>
            <span className="summary-card-value green">₹{parseFloat(data.balance || 0).toFixed(2)}</span>
          </div>
        </div>
      )}

      {loading ? (
        <div className="no-data"><p>लोड होत आहे...</p></div>
      ) : data.records.length > 0 ? (
        <>
          <div className="table-wrapper">
            <table className="emc-table">
              <thead>
                <tr><th>तारीख</th><th>प्रकार</th><th>पिशवी</th><th>दर</th><th>रक्कम</th><th>कपात</th></tr>
              </thead>
              <tbody>
                {data.records.map((r, i) => (
                  <tr key={i}>
                    <td>{fmtDate(r.date)}</td>
                    <td>{r.type || "-"}</td>
                    <td>{r.bags || "-"}</td>
                    <td>{r.rate || "-"}</td>
                    <td>{r.total_rate || "-"}</td>
                    <td>{r.debit || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="pagination">
              <button className="page-btn" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>&#8249;</button>
              <span className="page-info">{page} / {totalPages}</span>
              <button className="page-btn" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>&#8250;</button>
            </div>
          )}
        </>
      ) : (
        <div className="no-data"><p>कोणत्याही नोंदी उपलब्ध नाहीत.</p></div>
      )}

      {dlOpen && (
        <div className="dl-modal-overlay" onClick={() => setDlOpen(false)}>
          <div className="dl-modal" onClick={(e) => e.stopPropagation()}>
            <h3>📥 Excel डाउनलोड करा</h3>
            <p>कोणत्या नोंदी डाउनलोड करायच्या आहेत?</p>
            <div className="dl-modal-actions">
              <button className="dl-modal-btn page" onClick={handleDownloadPage}>फक्त हे पान ({data.records.length} नोंदी)</button>
              <button className="dl-modal-btn all" onClick={handleDownloadAll} disabled={dlLoading}>
                {dlLoading ? "लोड होत आहे..." : `सर्व नोंदी (${data.total} एकूण)`}
              </button>
              <button className="dl-modal-btn cancel" onClick={() => setDlOpen(false)}>रद्द करा</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplyView;
